/* BEATSEEK — Wall spotlight (mobile/tablet only, <1100px).
 * Two corner-cut animated-gradient links at the top of the Wall feed:
 *   ✦ Top Emotes (cyan/magenta)  ·  💡 Community Ideas (gold, unvoted badge)
 * Each opens a compact pop-up modal with the matching animated border.
 * Also listens for 'beatseek:open-featured-sidebar' (fired by the wall's
 * featured-emote card link) to open the emotes modal on mobile. */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, Lightbulb, X, Download, Wand2, BookmarkPlus } from 'lucide-react';
import { usePolls, PollCard } from './CommunityPollsStrip';
import { useBuildList, BuildListCard, BuildListEmpty } from './BuildListPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = ['#fbbf24', '#cbd5e1', '#fb923c'];

const WallSpotlightMobile = ({ currentUser }) => {
  const userId = currentUser?._id || currentUser?.id;
  const { polls, optedOut, vote, dismiss, setOptOutPref, unvotedCount } = usePolls(userId);
  const { items: buildItems, loading: buildLoading } = useBuildList();
  const [viewport, setViewport] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [openModal, setOpenModal] = useState(null); // 'emotes' | 'ideas' | null
  const [ideasTab, setIdeasTab] = useState('vote'); // 'vote' | 'build'
  const [emotes, setEmotes] = useState(null); // null = not yet fetched
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadEmotes = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/emote-lab/gallery?limit=3`);
      if (r.ok) {
        const d = await r.json();
        setEmotes(d.emotes || []);
      } else setEmotes([]);
    } catch (_e) { setEmotes([]); }
  }, []);

  // Wall featured-emote card "View top 3" link → open emotes modal on mobile
  useEffect(() => {
    if (viewport >= 1100) return;
    const handler = () => { setOpenModal('emotes'); };
    window.addEventListener('beatseek:open-featured-sidebar', handler);
    return () => window.removeEventListener('beatseek:open-featured-sidebar', handler);
  }, [viewport]);

  useEffect(() => {
    if (openModal === 'emotes' && emotes === null) loadEmotes();
  }, [openModal, emotes, loadEmotes]);

  if (viewport >= 1100) return null;

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const handleDownload = (emote) => {
    const a = document.createElement('a');
    a.href = `${API}/api/emote-lab/${emote.id}/file?d=1`;
    a.download = `${(emote.name || 'emote').replace(/[^a-z0-9_-]/gi, '_')}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    flash('Downloading…');
  };

  const handleRemix = (emote) => {
    setOpenModal(null);
    window.dispatchEvent(new CustomEvent('beatseek:open-emote-lab', {
      detail: { prompt: emote.prompt || emote.name || '', style: emote.style || 'neon' }
    }));
  };

  const handleSaveToLibrary = async (emote) => {
    if (!userId) { flash('Sign in first'); return; }
    try {
      const r = await fetch(`${API}/api/emote-lab/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, source_emote_id: emote.id }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Save failed');
      }
      flash('Saved to your library ✓');
    } catch (e) {
      flash(e.message || 'Save failed');
    }
  };

  const handleOptOut = () => {
    if (!window.confirm('Hide all community vote polls? You can turn them back on anytime from the Community Ideas link.')) return;
    setOptOutPref(true);
  };

  const modalTitle = openModal === 'emotes' ? 'Featured Emotes' : 'Community Ideas';

  return (
    <>
      {/* Spotlight links row — corner-cut cards, NOT pills */}
      <div className="wall-spotlight-links" data-testid="wall-spotlight-links">
        <button
          className="spotlight-link spotlight-link-cyan"
          data-testid="spotlight-emotes-link"
          onClick={() => setOpenModal('emotes')}
        >
          <span className="spotlight-link-inner">
            <Trophy size={13} style={{ color: '#fbbf24', flexShrink: 0 }} />
            <span className="spotlight-link-text-cyan">Top Emotes</span>
          </span>
        </button>
        <button
          className="spotlight-link spotlight-link-gold"
          data-testid="spotlight-ideas-link"
          onClick={() => setOpenModal('ideas')}
        >
          <span className="spotlight-link-inner">
            <Lightbulb size={13} style={{ color: '#facc15', flexShrink: 0 }} />
            <span className="spotlight-link-text-gold">Community Ideas</span>
            {unvotedCount > 0 && (
              <span className="spotlight-badge" data-testid="spotlight-ideas-badge">{unvotedCount}</span>
            )}
          </span>
        </button>
      </div>

      {/* Pop-up modal — portaled to body so the wall feed's transform doesn't clip the fixed overlay */}
      {openModal && createPortal(
        <div
          className="spotlight-modal-overlay"
          data-testid={`spotlight-${openModal}-modal`}
          onClick={() => setOpenModal(null)}
        >
          <div
            className={openModal === 'emotes' ? 'spotlight-cyan-card' : (ideasTab === 'build' ? 'build-green-card' : 'poll-gold-card')}
            onClick={e => e.stopPropagation()}
            style={{ width: 'min(92vw, 420px)', maxHeight: '80vh', display: 'flex' }}
          >
            <div
              className={openModal === 'emotes' ? 'spotlight-cyan-inner' : 'poll-gold-inner'}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '14px' }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexShrink: 0 }}>
                <span style={{
                  display: 'flex', alignItems: 'center', gap: '7px',
                  fontSize: '12px', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: openModal === 'emotes'
                    ? 'linear-gradient(135deg, #22d3ee, #e879f9)'
                    : 'linear-gradient(135deg, #facc15, #d97706)',
                  WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                  {openModal === 'emotes'
                    ? <Trophy size={14} style={{ color: '#fbbf24' }} />
                    : <Lightbulb size={14} style={{ color: '#facc15' }} />}
                  {modalTitle}
                </span>
                <button
                  data-testid="spotlight-modal-close"
                  onClick={() => setOpenModal(null)}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '4px', display: 'flex' }}
                >
                  <X size={18} />
                </button>
              </div>
              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '10px', letterSpacing: '0.05em', flexShrink: 0 }}>
                {openModal === 'emotes'
                  ? 'Top 3 picks from the community'
                  : ideasTab === 'build' ? 'You voted — watch it get built' : 'Vote on Beatseeker suggestions'}
              </div>

              {/* Ideas modal inner tabs: Vote | Build List */}
              {openModal === 'ideas' && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexShrink: 0 }}>
                  <button
                    data-testid="ideas-tab-vote"
                    onClick={() => setIdeasTab('vote')}
                    style={{
                      flex: 1, padding: '6px 8px', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                      background: ideasTab === 'vote' ? 'rgba(250,204,21,0.14)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${ideasTab === 'vote' ? 'rgba(250,204,21,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      color: ideasTab === 'vote' ? '#facc15' : '#64748b',
                      transition: 'all 0.15s',
                    }}
                  >
                    🗳️ Vote
                  </button>
                  <button
                    data-testid="ideas-tab-build"
                    onClick={() => setIdeasTab('build')}
                    style={{
                      flex: 1, padding: '6px 8px', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                      background: ideasTab === 'build' ? 'rgba(52,211,153,0.14)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${ideasTab === 'build' ? 'rgba(52,211,153,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      color: ideasTab === 'build' ? '#34d399' : '#64748b',
                      transition: 'all 0.15s',
                    }}
                  >
                    🔨 Build List
                  </button>
                </div>
              )}

              {/* Scrollable body */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {openModal === 'emotes' ? (
                  emotes === null ? (
                    <div style={{ padding: '20px 0', textAlign: 'center', color: '#475569', fontSize: '12px' }}>Loading…</div>
                  ) : emotes.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#475569',
                                  border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '10px' }}>
                      No public emotes yet. Be the first to publish one!
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {emotes.map((e, i) => (
                        <div
                          key={e.id}
                          data-testid={`mobile-emote-${e.id}`}
                          style={{
                            position: 'relative', padding: '10px',
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(34,211,238,0.16)',
                            borderRadius: '12px',
                          }}
                        >
                          <div style={{
                            position: 'absolute', top: '4px', right: '10px',
                            fontSize: '22px', fontWeight: 900, lineHeight: 1,
                            color: RANK_COLORS[i] || '#94a3b8',
                            textShadow: `0 0 10px ${RANK_COLORS[i] || '#94a3b8'}66, 0 2px 4px rgba(0,0,0,0.6)`,
                            pointerEvents: 'none',
                          }}>{i + 1}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '58px', height: '58px', flexShrink: 0,
                              borderRadius: '9px', overflow: 'hidden',
                              background: '#0a0a14', border: '1px solid rgba(34,211,238,0.14)',
                            }}>
                              <img
                                src={`${API}/api/emote-lab/${e.id}/file`}
                                alt={e.name}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '22px' }}>
                                {e.name}
                              </div>
                              <div style={{ fontSize: '9px', color: '#64748b', marginTop: '2px',
                                            textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                {e.style}
                              </div>
                              <div style={{ fontSize: '10px', color: '#22d3ee', marginTop: '2px', fontWeight: 700 }}>
                                ⬇ {e.downloads || 0}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                            <button data-testid={`mobile-emote-download-${e.id}`} onClick={() => handleDownload(e)} style={emoteBtn('#22d3ee')}>
                              <Download size={11} /> PNG
                            </button>
                            <button data-testid={`mobile-emote-remix-${e.id}`} onClick={() => handleRemix(e)} style={emoteBtn('#e879f9')}>
                              <Wand2 size={11} /> Remix
                            </button>
                            <button data-testid={`mobile-emote-save-${e.id}`} onClick={() => handleSaveToLibrary(e)} style={emoteBtn('#fbbf24')}>
                              <BookmarkPlus size={11} /> Save
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : ideasTab === 'build' ? (
                  buildLoading ? (
                    <div style={{ padding: '20px 0', textAlign: 'center', color: '#475569', fontSize: '12px' }}>Loading…</div>
                  ) : buildItems.length === 0 ? (
                    <BuildListEmpty />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {buildItems.map(it => <BuildListCard key={it.poll_id} item={it} />)}
                    </div>
                  )
                ) : optedOut ? (
                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#94a3b8',
                                border: '1px dashed rgba(250,204,21,0.2)', borderRadius: '10px' }}>
                    <div style={{ marginBottom: '10px' }}>Community votes are hidden.</div>
                    <button
                      onClick={() => setOptOutPref(false)}
                      data-testid="poll-opt-in-btn"
                      style={{ background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.4)',
                               color: '#facc15', fontSize: '11px', fontWeight: 700, padding: '7px 14px',
                               borderRadius: '7px', cursor: 'pointer' }}
                    >
                      Show community votes
                    </button>
                  </div>
                ) : polls.length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#475569',
                                border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '10px' }}>
                    <div style={{ marginBottom: '4px' }}>No active community votes right now.</div>
                    <div>Drop an idea in the Suggestion Box on your Dashboard!</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {polls.map(p => <PollCard key={p.id} poll={p} onVote={vote} />)}
                  </div>
                )}
              </div>

              {openModal === 'ideas' && ideasTab === 'vote' && !optedOut && (
                <div style={{ textAlign: 'right', marginTop: '8px', flexShrink: 0 }}>
                  <button
                    onClick={handleOptOut}
                    data-testid="poll-opt-out-btn"
                    style={{ background: 'none', border: 'none', color: '#565b70', fontSize: '0.65rem', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Hide community votes
                  </button>
                </div>
              )}

              {toast && (
                <div style={{
                  textAlign: 'center', marginTop: '8px', flexShrink: 0,
                  fontSize: '10px', color: '#22d3ee', fontWeight: 700, letterSpacing: '0.05em',
                }}>
                  {toast}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const emoteBtn = (color) => ({
  flex: 1, padding: '7px 4px',
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${color}`,
  color, fontSize: '10px', fontWeight: 700,
  letterSpacing: '0.03em',
  borderRadius: '7px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
  transition: 'all 0.15s',
});

export default WallSpotlightMobile;
